import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Modal, Button, TextInput, Select, Badge } from '@kardume/ui';
import { useNotification } from '@kardume/hooks';
import { backofficeApi, extractErrorMessage } from '@kardume/services';
import type {
  MerchantImportPreviewDto,
  MerchantImportRequestDto,
  MerchantSource,
} from '@kardume/types';

/**
 * Importação de empresas em lote (card 231, regras §38.3).
 *
 * Dois passos na tela, e a ordem é a razão de a tela existir: **pré-visualizar
 * antes de gravar**. Sem isso, um cabeçalho errado só apareceria depois de mil
 * empresas criadas - e desfazer mil cadastros à mão é o tipo de trabalho que
 * ninguém faz, então elas ficariam lá.
 *
 * A **base legal** é campo obrigatório e escrito por quem sobe o arquivo. Dado
 * de terceiro só entra se a licença permitir redistribuição, e essa afirmação
 * precisa ter dono e data - não é burocracia, é a diferença entre uma
 * importação defensável e uma que ninguém consegue explicar depois.
 */
const SOURCE_OPTIONS: { value: MerchantSource; label: string }[] = [
  { value: 'IMPORT', label: 'Importação autorizada' },
  { value: 'GUIDE', label: 'Curadoria do guia' },
  { value: 'BACKOFFICE', label: 'Equipe Kardume' },
];

const empty: MerchantImportRequestDto = {
  content: '',
  source: 'IMPORT',
  sourceReference: '',
  legalBasis: '',
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onRegistered: () => void;
}

const MerchantImportModal: React.FC<Props> = ({ isOpen, onClose, onRegistered }) => {
  const { showNotification } = useNotification();
  const [form, setForm] = useState<MerchantImportRequestDto>(empty);
  const [preview, setPreview] = useState<MerchantImportPreviewDto | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setForm(empty);
      setPreview(null);
    }
  }, [isOpen]);

  const set = <K extends keyof MerchantImportRequestDto>(
    key: K,
    value: MerchantImportRequestDto[K],
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    // Mexeu no arquivo ou na procedência, a pré-visualização anterior deixou de
    // valer. Mantê-la na tela seria oferecer um "registrar" apoiado num
    // relatório de outro conteúdo.
    setPreview(null);
  };

  const canPreview = form.content.trim().length > 0;
  const canRegister =
    !!preview && preview.acceptedRows > 0 &&
    form.sourceReference.trim().length > 0 && form.legalBasis.trim().length > 0;

  const runPreview = () => {
    setBusy(true);
    backofficeApi
      .previewMerchantImport(form)
      .then(setPreview)
      .catch((e) => showNotification(extractErrorMessage(e, 'Não foi possível ler o arquivo.'), 'error'))
      .finally(() => setBusy(false));
  };

  const register = () => {
    setBusy(true);
    backofficeApi
      .registerMerchantImport(form)
      .then(() => {
        showNotification('Lote registrado. O processamento acontece em segundo plano.', 'success');
        onRegistered();
        onClose();
      })
      .catch((e) => showNotification(extractErrorMessage(e, 'Não foi possível registrar o lote.'), 'error'))
      .finally(() => setBusy(false));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Importar empresas"
      width="720px"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="secondary" disabled={!canPreview || busy} onClick={runPreview}>
            Pré-visualizar
          </Button>
          <Button variant="primary" disabled={!canRegister || busy} isLoading={busy} onClick={register}>
            Registrar lote
          </Button>
        </>
      }
    >
      <Hint>
        Colunas obrigatórias: <code>nome</code> e <code>categoria_id</code>. Opcionais:{' '}
        <code>nome_fantasia</code>, <code>cidade</code>, <code>uf</code>, <code>bairro</code>,{' '}
        <code>endereco</code>, <code>telefone</code>, <code>whatsapp</code>, <code>site</code>,{' '}
        <code>instagram</code>. As empresas entram <strong>sem parceria</strong>, e linhas com
        nome parecido na mesma cidade são recusadas com o motivo.
      </Hint>

      <Grid>
        <Field>
          <label htmlFor="import-source">Origem *</label>
          <Select
            id="import-source"
            options={SOURCE_OPTIONS}
            value={form.source}
            onChange={(e) => set('source', e.target.value as MerchantSource)}
          />
        </Field>
        <Field>
          <label htmlFor="import-ref">Fonte *</label>
          <TextInput
            id="import-ref"
            value={form.sourceReference}
            onChange={(e) => set('sourceReference', e.target.value)}
            placeholder="Ex.: lista da prefeitura, jan/2026"
          />
        </Field>
        <Field className="full">
          <label htmlFor="import-legal">Base legal *</label>
          <TextInput
            id="import-legal"
            value={form.legalBasis}
            onChange={(e) => set('legalBasis', e.target.value)}
            placeholder="Ex.: dados públicos, licença aberta permitindo redistribuição"
          />
        </Field>
        <Field className="full">
          <label htmlFor="import-content">Conteúdo (CSV) *</label>
          <TextArea
            id="import-content"
            rows={8}
            value={form.content}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => set('content', e.target.value)}
            placeholder={'nome,categoria_id,cidade,uf\nPadaria do Centro,<uuid>,Toledo,PR'}
          />
        </Field>
      </Grid>

      {preview && (
        <Preview>
          <div className="summary">
            <Badge tone="success">{preview.acceptedRows} aceitas</Badge>
            <Badge tone={preview.rejectedRows > 0 ? 'danger' : 'secondary'}>
              {preview.rejectedRows} rejeitadas
            </Badge>
            <span>{preview.totalRows} linhas no arquivo</span>
          </div>
          {/* Só as recusadas: as aceitas o operador já vai ver virarem empresa.
              O que ele precisa daqui é a lista do que corrigir. */}
          {preview.rejectedRows > 0 && (
            <ul>
              {preview.rows
                .filter((row) => row.status === 'REJECTED')
                .slice(0, 50)
                .map((row) => (
                  <li key={row.lineNumber}>
                    <strong>Linha {row.lineNumber}</strong> - {row.rejectionReason}
                  </li>
                ))}
            </ul>
          )}
        </Preview>
      )}
    </Modal>
  );
};

export default MerchantImportModal;

/* -------------------------------------------------------------------------- */

const Hint = styled.p`
  margin: 0 0 1rem;
  font-size: 0.82rem;
  line-height: 1.5;
  color: ${(p) => p.theme.textMuted};

  code {
    font-size: 0.78rem;
    padding: 1px 4px;
    border-radius: 4px;
    background: ${(p) => p.theme.surfaceAlt ?? p.theme.surface};
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.85rem;

  .full {
    grid-column: 1 / -1;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;

  label {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${(p) => p.theme.textMuted};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.6rem 0.75rem;
  border-radius: ${(p) => p.theme.radius};
  border: 1px solid ${(p) => p.theme.border};
  background: ${(p) => p.theme.surface};
  color: ${(p) => p.theme.textPrimary};
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.8rem;
  line-height: 1.5;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${(p) => p.theme.primary};
  }
`;

const Preview = styled.div`
  margin-top: 1rem;

  .summary {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.82rem;
    color: ${(p) => p.theme.textMuted};
  }
  ul {
    margin: 0.75rem 0 0;
    padding-left: 1.1rem;
    max-height: 220px;
    overflow-y: auto;
    font-size: 0.82rem;
    color: ${(p) => p.theme.textPrimary};
  }
  li {
    margin-bottom: 0.25rem;
  }
`;
